import React, { useEffect, useMemo, useState } from "react"
import _ from "lodash"
import { useNavigate, useOutlet } from "react-router-dom"
import { Button, InputAdornment, LinearProgress, TextField } from "@mui/material"
import {
    GroupsOutlined,
    PaymentsOutlined,
    RefreshOutlined,
    SchoolOutlined,
    Search,
    SearchOff
} from "@mui/icons-material"

import { GetCurrentUser, generateRandomId } from "../utils/hooks"
import { useSnackbar } from "../components/Snakebar"
import ClassList from "../components/ClassList"
import EmptyState from "../Lectures/components/EmptyState"
import { matchesSearch, saveBlob, sortByRollNo } from "../StockManager/classroom/helpers"
import FeeStudentList from "./fee/FeeStudentList"
import StudentFeeDialog from "./fee/StudentFeeDialog"
import {
    MONTHLY_FEE_NAME,
    MONTHS,
    formatMoney,
    monthlyFeeItems,
    paymentDateFor,
    sumFees
} from "./fee/helpers"

import { useLazyGetInstitutionClassesQuery } from "../Redux/actions/classSetup.action"
import { useLazyGetClassStudentsQuery } from "../Redux/actions/studentAction"
import { useLazyGetUserInstitutionQuery } from "../Redux/actions/setting.action"
import { useDownloadFeeReceiptMutation } from "../Redux/actions/download.action"
import { useCollectFeeMutation, useLazyGetStudentPaidDataQuery } from "../Redux/actions/feeCollection.action"

const pillButtonSx = { textTransform: "none", borderRadius: "10px", whiteSpace: "nowrap" }

export default function FeeRoot() {
    const navigate = useNavigate()
    // The legacy /fee/:class_name screen renders on its own
    const outlet = useOutlet()
    const { showSnackbar } = useSnackbar()
    const schoolId = useMemo(() => _.get(GetCurrentUser(), "meta_data.user_platform", null), [])

    const [activeClassId, setActiveClassId] = useState(null)
    const [search, setSearch] = useState("")
    const [feeStudent, setFeeStudent] = useState(null)
    const [collecting, setCollecting] = useState(false)
    const [receiptBusyMonth, setReceiptBusyMonth] = useState(null)

    const [getClasses, classesState] = useLazyGetInstitutionClassesQuery()
    const [getClassStudents, studentsState] = useLazyGetClassStudentsQuery()
    const [getUserSchool, schoolState] = useLazyGetUserInstitutionQuery()
    const [getPaidData, paidDataState] = useLazyGetStudentPaidDataQuery()
    const [collectFee] = useCollectFeeMutation()
    const [downloadFeeReceipt] = useDownloadFeeReceiptMutation()

    /* ------------------------------ data ------------------------------ */

    const classes = useMemo(() => {
        const list = _.get(classesState, "data.list", [])
        return Array.isArray(list) ? list : []
    }, [classesState])

    const activeClass = useMemo(
        () => classes.find((item) => item.id === activeClassId) || null,
        [classes, activeClassId]
    )

    const feeItems = useMemo(() => monthlyFeeItems(activeClass), [activeClass])
    const monthlyTotal = sumFees(feeItems)

    const students = useMemo(() => {
        if (!activeClass) return []
        const list = _.get(studentsState, "currentData.list", [])
        return Array.isArray(list) ? sortByRollNo(list) : []
    }, [activeClass, studentsState])

    const visibleStudents = useMemo(
        () => students.filter((student) => matchesSearch(student, search)),
        [students, search]
    )

    const school = useMemo(() => {
        if (!schoolState.isSuccess) return null
        const info = _.get(schoolState, "currentData.data", null)
        if (!info) return null
        return {
            school_name: _.get(info, "name", ""),
            school_address: `${_.get(info, "state", "")} ${_.get(info, "district", "")} ${_.get(info, "address", "")} ${_.get(info, "pin", "")}`
        }
    }, [schoolState])

    const payments = useMemo(() => {
        if (!feeStudent || !paidDataState.isSuccess) return []
        const list = _.get(paidDataState, "currentData.data", [])
        return Array.isArray(list) ? list : []
    }, [feeStudent, paidDataState])

    /* ----------------------------- fetching ---------------------------- */

    const fetchClasses = () => {
        if (schoolId) getClasses({ institution_ref: schoolId })
    }

    const fetchStudents = (item = activeClass) => {
        if (!item?.id || !item?.institution_ref) return
        getClassStudents({ class_id: item.id, school_id: item.institution_ref })
    }

    const fetchPayments = (student = feeStudent) => {
        if (!student) return
        getPaidData({ school_id: student.school_ref, student_id: student.id })
    }

    useEffect(() => {
        if (!GetCurrentUser()) {
            navigate("/")
            return
        }
        if (!schoolId) return
        fetchClasses()
        getUserSchool(schoolId)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const selectClass = (item) => {
        setActiveClassId(item.id)
        setSearch("")
        fetchStudents(item)
    }

    // Open the first class once classes arrive, or if the open one disappears
    useEffect(() => {
        if (classes.length > 0 && !activeClass) selectClass(classes[0])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classes])

    const refresh = () => {
        fetchClasses()
        fetchStudents()
    }

    /* ------------------------------ fees ------------------------------ */

    const openFees = (student) => {
        setFeeStudent(student)
        fetchPayments(student)
    }

    // Each month is its own payment record, dated inside the month it pays for
    const collectMonths = async ({ months, year, amount }) => {
        if (!feeStudent || months.length === 0 || amount <= 0) return

        setCollecting(true)
        const results = await Promise.allSettled(
            months.map((month) =>
                collectFee({
                    school_ref: feeStudent.school_ref,
                    student_ref: feeStudent.id,
                    fee_name: MONTHLY_FEE_NAME,
                    fee_amount: amount,
                    payment_date: paymentDateFor(month, year)
                }).unwrap()
            )
        )
        setCollecting(false)

        const failed = months.filter((_month, index) => results[index].status === "rejected")
        if (failed.length === 0) {
            showSnackbar({
                message: `Collected ${formatMoney(amount * months.length)} for ${months.length} month${months.length === 1 ? "" : "s"}.`,
                severity: "success"
            })
        } else {
            showSnackbar({
                message: `Couldn't record ${failed.map((month) => MONTHS[month - 1]).join(", ")}. Please try again.`,
                severity: "error"
            })
        }
        fetchPayments()
    }

    const downloadReceipt = async ({ month, year, entry }) => {
        if (!entry || !feeStudent || !activeClass) return
        if (!school) {
            showSnackbar({ message: "School details are still loading. Please try again.", severity: "info" })
            return
        }

        setReceiptBusyMonth(`${year}-${month}`)
        try {
            const blob = await downloadFeeReceipt({
                ...school,
                receipt_title: "Monthly Fee Payment Receipt",
                student_name: feeStudent.name,
                class_name: activeClass.class_name,
                application_no: generateRandomId(14),
                receipt_date: entry.date,
                roll_no: feeStudent.roll_no,
                fee_items: entry.payments.map((payment) => ({
                    name: `Monthly fee - ${MONTHS[month - 1]} ${year}`,
                    amount: payment.fee_amount
                }))
            }).unwrap()
            saveBlob(blob instanceof Blob ? blob : new Blob([blob]), `fee-receipt-${MONTHS[month - 1]}-${year}.pdf`)
        } catch {
            showSnackbar({ message: "Couldn't download the receipt. Please try again.", severity: "error" })
        } finally {
            setReceiptBusyMonth(null)
        }
    }

    /* ------------------------------- view ------------------------------- */

    if (outlet) return outlet

    const loadingClasses = classesState.isLoading || classesState.isFetching
    const loadingStudents = studentsState.isFetching

    const renderStudents = () => {
        if (studentsState.isError && !loadingStudents) {
            return (
                <div className="p-4 sm:p-6">
                    <EmptyState
                        icon={<GroupsOutlined />}
                        title="Couldn't load students"
                        description="Please check your connection and try again."
                        actionLabel="Retry"
                        onAction={() => fetchStudents()}
                    />
                </div>
            )
        }

        if (loadingStudents && students.length === 0) {
            return (
                <div className="space-y-3 p-5">
                    {[0, 1, 2, 3, 4].map((key) => (
                        <div key={key} className="h-12 animate-pulse rounded-lg bg-slate-100" />
                    ))}
                </div>
            )
        }

        if (students.length === 0) {
            return (
                <div className="p-4 sm:p-6">
                    <EmptyState
                        icon={<GroupsOutlined />}
                        title="No students in this class"
                        description="Students added to this class will show up here for fee collection."
                    />
                </div>
            )
        }

        if (visibleStudents.length === 0) {
            return (
                <div className="p-4 sm:p-6">
                    <EmptyState
                        icon={<SearchOff />}
                        title="No students match"
                        description="Try a different name, roll number or email."
                        actionLabel="Clear search"
                        onAction={() => setSearch("")}
                    />
                </div>
            )
        }

        return <FeeStudentList students={visibleStudents} onCollect={openFees} />
    }

    return (
        <div className="mx-auto w-full max-w-[1400px] px-4 pb-24 pt-6 sm:px-6 lg:px-8">

            {/* Page header */}
            <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600">
                        Fee management
                    </p>
                    <h1 className="mt-1 text-2xl font-bold text-slate-900 sm:text-[28px]">
                        Monthly Fees
                    </h1>
                    <p className="mt-1 max-w-xl text-sm text-slate-500">
                        Pick a class and a student to see which months are paid, collect dues and download receipts.
                    </p>
                </div>
                <Button
                    variant="outlined"
                    color="inherit"
                    startIcon={<RefreshOutlined />}
                    onClick={refresh}
                    disabled={loadingClasses || loadingStudents}
                    sx={{ ...pillButtonSx, borderColor: "#e2e8f0", color: "#334155" }}
                >
                    Refresh
                </Button>
            </header>

            {!loadingClasses && classes.length === 0 ? (
                <EmptyState
                    icon={<SchoolOutlined />}
                    title={classesState.isError ? "Couldn't load classes" : "No classes found"}
                    description={
                        classesState.isError
                            ? "Please check your connection and try again."
                            : "Create a class first to start collecting fees."
                    }
                    actionLabel={classesState.isError ? "Retry" : undefined}
                    onAction={classesState.isError ? fetchClasses : undefined}
                />
            ) : (
                <div className="grid gap-6 lg:grid-cols-[240px_minmax(0,1fr)]">

                    {/* Classes */}
                    <aside className="lg:sticky lg:top-4 lg:self-start">
                        <div className="mb-3 flex items-center justify-between">
                            <h2 className="text-sm font-semibold text-slate-700">Classes</h2>
                            <span className="text-xs tabular-nums text-slate-400">{classes.length}</span>
                        </div>
                        <ClassList
                            classes={classes}
                            activeId={activeClassId}
                            onSelect={selectClass}
                            loading={loadingClasses}
                            getMeta={(item) => {
                                const total = sumFees(monthlyFeeItems(item))
                                return total > 0 ? `${formatMoney(total)} / month` : "No monthly fee"
                            }}
                        />
                    </aside>

                    {/* Students of the selected class */}
                    <section className="min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                        <div className="h-1">
                            {loadingStudents && <LinearProgress sx={{ height: 3 }} />}
                        </div>

                        <div className="flex flex-wrap items-start justify-between gap-4 px-4 pt-4 sm:px-5">
                            <div className="min-w-0">
                                <h2 className="truncate text-lg font-semibold text-slate-900">
                                    {activeClass?.class_name || "Select a class"}
                                </h2>
                                <p className="text-sm text-slate-500">
                                    {students.length} student{students.length === 1 ? "" : "s"}
                                </p>
                            </div>
                            <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                                    <PaymentsOutlined fontSize="small" />
                                </span>
                                <div>
                                    <div className="text-xs text-slate-500">Monthly fee per student</div>
                                    <div className="font-semibold tabular-nums text-slate-900">
                                        {monthlyTotal > 0 ? formatMoney(monthlyTotal) : "Not set"}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {feeItems.length > 0 && (
                            <div className="flex flex-wrap gap-2 px-4 pt-3 sm:px-5">
                                {feeItems.map((item) => (
                                    <span
                                        key={item.feeName}
                                        className="rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-xs text-slate-600"
                                    >
                                        {item.feeName}
                                        <span className="ml-1.5 font-semibold tabular-nums text-slate-800">
                                            {formatMoney(item.feeAmount)}
                                        </span>
                                    </span>
                                ))}
                            </div>
                        )}

                        {/* Search */}
                        <div className="mt-4 border-y border-slate-100 px-4 py-3 sm:px-5">
                            <TextField
                                size="small"
                                fullWidth
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by name, roll number or email"
                                inputProps={{ "aria-label": "Search students" }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search fontSize="small" />
                                        </InputAdornment>
                                    )
                                }}
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                            />
                        </div>

                        {renderStudents()}
                    </section>
                </div>
            )}

            <StudentFeeDialog
                open={Boolean(feeStudent && activeClass)}
                onClose={() => setFeeStudent(null)}
                student={feeStudent}
                classItem={activeClass}
                feeItems={feeItems}
                payments={payments}
                loading={paidDataState.isFetching}
                collecting={collecting}
                receiptBusyMonth={receiptBusyMonth}
                onCollect={collectMonths}
                onReceipt={downloadReceipt}
            />
        </div>
    )
}
